import React, { useMemo } from "react";

import { useIsSSR } from "react-aria";

import { css } from "@emotion/react";
import styled from "@emotion/styled";

import CarouselComponent from "../../CarouselComponent";
import { contentContainer } from "../../ContentContainer";
import Image from "../../Image";
import { MarkdownWithLessParagraphPadding } from "../../Markdown";
import Headline from "../../NewHeadline";
import { SideNavContainer } from "../SideNavContainer";
import useSideNavStore from "../SideNavContainer/store";

const Container = styled.div`
    ${contentContainer};
`;

const CarouselItem = styled.div`
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;

    ${({ theme }) => theme.breakpoints.from.medium.css`
        padding-right: 30px;
        padding-left: 30px;
    `}
    ${({ theme }) => theme.breakpoints.from.large.css`
        padding-right: 50px;
        padding-left: 50px;
    `}
`;

const Item = styled.div`
    display: flex;
    flex-direction: column;
`;

const headlineStyles = css`
    margin-top: 20px;
    margin-bottom: 20px;
`;

const TextBlock = styled.div`
    color: ${({ theme }) => theme.colors.blue.toString()};
`;

const SublineBlock = styled.div`
    margin-top: 20px;
    font-size: smaller;
    color: ${({ theme }) => theme.colors.blue.toString()};
`;

const StyledImage = styled(Image)`
    width: 250px;
    pointer-events: none;
`;

export const Dot = styled.div`
    width: 10px;
    height: 10px;
    margin: 5px;
    background-color: ${({ active, theme }) =>
        active ? theme.colors.red.toString() : "transparent"};
    border: 1px solid ${({ theme }) => theme.colors.red.toString()};
    border-radius: 50%;

    &:hover {
        cursor: pointer;
    }
`;

const renderDotsItem = ({ isActive }) => {
    return <Dot active={isActive} />;
};

const CarouselElement = ({
    items,
    itemsMobile,
    itemsTablet,
    itemsDesktop,
    itemsDesktopLarge,
    isItemCenter,
    textAlign = undefined,
}) => {
    const isSsr = useIsSSR();
    const [paddingLeft] = useSideNavStore((state) => [state.paddingLeft]);

    const isImgOnlyCarousel =
        items.filter((item) => item.name || item.text || item.subline)
            .length === 0;

    const responsiveImageCarousel = {
        0: { items: itemsMobile ? itemsMobile : 1 },
        568: { items: itemsTablet ? itemsTablet : 2 },
        1024: { items: itemsDesktop ? itemsDesktop : 5 },
        1280: { items: itemsDesktopLarge ? itemsDesktopLarge : 5 },
    };

    const responsiveMixedCarousel = {
        0: { items: itemsMobile ? itemsMobile : 1 },
        568: { items: itemsTablet ? itemsTablet : 2 },
        1024: { items: itemsDesktop ? itemsDesktop : 4 },
    };

    //Always center the content on mobile otherwise it looks weird.
    const CarouselItemStyle = styled.div`
        display: flex;
        flex-direction: column;
        align-self: ${isItemCenter ? "center" : "start"};
        text-align: ${textAlign ? textAlign : "start"};
        ${({ theme }) => theme.breakpoints.upTo.small.css`
            align-self: center;
        `}
    `;

    const imageStyles = css`
        ${isImgOnlyCarousel &&
        css`
            max-width: 200px;

            & > div {
                margin: 0 auto;
            }
        `}
    `;

    const imageWrapperStyles = css`
        ${isImgOnlyCarousel &&
        css`
            & > div {
                margin: 0 auto;
            }
        `}
    `;

    const carouselItems = useMemo(
        () =>
            items.length > 0 &&
            items.map((item, i) => {
                const itemComponent = (
                    <Item>
                        <CarouselItemStyle>
                            {item.image?.src && (
                                <div css={imageWrapperStyles}>
                                    <StyledImage
                                        css={imageStyles}
                                        {...item.image}
                                    />
                                </div>
                            )}
                            {item.name && (
                                <Headline css={headlineStyles}>
                                    {item.name}
                                </Headline>
                            )}
                            {item.text && (
                                <TextBlock>
                                    <MarkdownWithLessParagraphPadding>
                                        {item.text}
                                    </MarkdownWithLessParagraphPadding>
                                </TextBlock>
                            )}
                            {item.sublineString && (
                                <SublineBlock>
                                    {item.sublineString}
                                </SublineBlock>
                            )}
                        </CarouselItemStyle>
                    </Item>
                );

                return (
                    <CarouselItem key={i}>
                        {item.linkOptional && item.linkOptional.href ? (
                            <a
                                href={item.linkOptional.href}
                                rel="noopener noreferrer noodp"
                                target="_blank"
                            >
                                {itemComponent}
                            </a>
                        ) : (
                            itemComponent
                        )}
                    </CarouselItem>
                );
            }),
        [items]
    );

    if (isSsr) {
        return null;
    }

    return (
        <Container>
            <SideNavContainer paddingLeft={paddingLeft}>
                <CarouselComponent
                    mouseTracking
                    items={carouselItems}
                    responsive={
                        isImgOnlyCarousel
                            ? responsiveImageCarousel
                            : responsiveMixedCarousel
                    }
                    infinite
                    disableButtonsControls
                    renderDotsItem={renderDotsItem}
                    controlsStrategy="alternate"
                />
            </SideNavContainer>
        </Container>
    );
};

export default CarouselElement;
